import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Collapse, Space, Tag, Tooltip, Typography } from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { QuoteLineItemProps } from '@partsbadger/types';

import { Upload3dFile } from './UploadParts';
import QuoteStore from '../stores/QuoteStore';
import { describeAnalysisError } from '../utils/analysisError';

const { Text } = Typography;

type AnalysisStatusDetails = QuoteLineItemProps['product']['analysis_status']['file_3d'];

interface FileAnalysisAlertProps {
    fileType: '3D' | '2D';
    item: QuoteLineItemProps;
    analysisStatus: AnalysisStatusDetails;
    /** `inline` is a compact tag for the file row; `block` is the full message with actions. */
    variant?: 'inline' | 'block';
}

export const FileAnalysisAlert: React.FC<FileAnalysisAlertProps> = observer(
    ({ fileType, item, analysisStatus, variant = 'block' }) => {
        const [restarting, setRestarting] = useState(false);
        const canReanalyze = QuoteStore.user_is_staff;
        const copy = describeAnalysisError(fileType, analysisStatus?.message, { canRetry: canReanalyze });

        if (variant === 'inline') {
            return (
                <Tooltip title={copy.title}>
                    <Tag color="error" icon={<ExclamationCircleOutlined />} className="mb-0">
                        Analysis failed
                    </Tag>
                </Tooltip>
            );
        }

        const reanalyze = async () => {
            setRestarting(true);
            try {
                await QuoteStore.reanalyzeMasterProduct(item.master_product_details.id);
            } finally {
                setRestarting(false);
            }
        };

        return (
            <Alert
                type="error"
                showIcon
                data-error-kind={copy.kind}
                data-file-type={fileType.toLowerCase()}
                data-testid="file-analysis-alert"
                message={<Text strong>{copy.title}</Text>}
                description={
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Text>{copy.detail}</Text>

                        {(canReanalyze || fileType === '3D') && (
                            <Space wrap size={8}>
                                {canReanalyze && (
                                    <Button
                                        size="small"
                                        icon={<ReloadOutlined />}
                                        loading={restarting}
                                        data-testid="file-analysis-reanalyze"
                                        onClick={reanalyze}
                                    >
                                        Analyze again
                                    </Button>
                                )}
                                {fileType === '3D' && (
                                    <Upload3dFile
                                        item={item}
                                        buttonType="compact"
                                        buttonText="Upload a new file"
                                        data-testid="file-analysis-upload"
                                    />
                                )}
                            </Space>
                        )}

                        {QuoteStore.user_is_staff && copy.technical && (
                            <Collapse ghost>
                                <Collapse.Panel
                                    key="details"
                                    header={
                                        <Text type="secondary" data-testid="file-analysis-technical">
                                            Technical details (staff only)
                                        </Text>
                                    }
                                >
                                    <Text code copyable style={{ fontSize: 12, wordBreak: 'break-word' }}>
                                        {copy.technical}
                                    </Text>
                                </Collapse.Panel>
                            </Collapse>
                        )}
                    </Space>
                }
            />
        );
    }
);
